import { useCallback, useEffect, useState } from "react";
import customAxios, { isCanceled, makeCancelable } from "lib/axios";

const EMPTY_COUNTS = {
  total: 0,
  holidays: 0,
  low: 0,
  medium: 0,
  high: 0,
};

const ALL_IMPACTS = ["2", "1", "0", "-1"];

const serializeImpacts = (impact) => {
  if (!Array.isArray(impact)) return impact;

  const selected = [...new Set(impact.map(String))];
  if (selected.length === 0) return "none";
  if (ALL_IMPACTS.every((value) => selected.includes(value))) return "all";
  return selected.join(",");
};

const serializeCurrencies = (currency) => {
  if (!Array.isArray(currency)) return currency;

  const selected = [...new Set(currency.map(String))];
  return selected.length > 0 ? selected.join(",") : "all";
};

const useEconomicCalendar = (date, impact = "all", currency = "all") => {
  const impactParam = serializeImpacts(impact);
  const currencyParam = serializeCurrencies(currency);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => {
    setReloadKey((value) => value + 1);
  }, []);

  useEffect(() => {
    const cancelable = makeCancelable();
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await customAxios.get("/news/economic-calendar", {
          params: { date, impact: impactParam, currency: currencyParam },
          ...cancelable,
        });

        if (!active) return;
        setItems(
          Array.isArray(response.data?.items) ? response.data.items : []
        );
        setCounts({ ...EMPTY_COUNTS, ...(response.data?.counts || {}) });
        setCurrencies(
          Array.isArray(response.data?.currencies)
            ? response.data.currencies
            : []
        );
      } catch (requestError) {
        if (!active || isCanceled(requestError)) return;
        setItems([]);
        setCounts(EMPTY_COUNTS);
        setCurrencies([]);
        setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
      cancelable.cancel();
    };
  }, [date, impactParam, currencyParam, reloadKey]);

  return { items, counts, currencies, loading, error, reload };
};

export default useEconomicCalendar;
